import React, { useState, useEffect, useCallback } from 'react';
import useStickyPin from '../hooks/useStickyPin';
import styled from 'styled-components';
import axios from 'axios';
import { getLocalProfilePicUrl } from '../config/imageUrls';
import {
  Contact, Search, Plus, Filter, ChevronDown, RefreshCw,
  Phone, Mail, MapPin, Instagram, Linkedin, MessageCircle,
  Calendar, Tag, Building2, DollarSign, ExternalLink,
  Edit3, Trash2, Eye, ArrowUpDown, X, Check,
  Users, AlertCircle, Upload, FileSpreadsheet,
  Sparkles, Send, MessageSquare, Globe, Save, Undo2,
  CheckSquare, Square, Loader2, LayoutList, LayoutGrid, UserCircle, Hash,
  ChevronRight
} from 'lucide-react';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`width: 100%; padding: 0;`;

const TopBar = styled.div`
  display: flex; align-items: center; gap: 12px;
  padding: 16px 24px; border-bottom: 1px solid var(--border);
  background: var(--card-bg);
`;

const Title = styled.h1`
  font-size: 18px; font-weight: 800; color: var(--text-primary);
  display: flex; align-items: center; gap: 8px; margin: 0;
  svg { color: var(--primary); }
`;

const Spacer = styled.div`flex: 1;`;

const SearchBar = styled.div`
  display: flex; align-items: center; gap: 8px;
  background: var(--surface); border: 1px solid var(--border);
  border-radius: 8px; padding: 6px 12px; width: 280px;
  &:focus-within { border-color: var(--primary); }
  input { border: none; background: none; outline: none; flex: 1;
    font-size: 12px; color: var(--text-primary); }
`;

const SmallBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 8px; border: 1px solid var(--border);
  background: ${p => p.$primary ? 'var(--primary)' : 'var(--card-bg)'};
  color: ${p => p.$primary ? '#fff' : 'var(--text-secondary)'};
  font-size: 11px; font-weight: 600; cursor: pointer; transition: all .15s;
  &:hover { border-color: var(--primary); opacity: .9; }
  &:disabled { opacity: .4; cursor: not-allowed; }
`;

const StatsRow = styled.div`
  display: flex; gap: 12px; padding: 12px 24px;
  border-bottom: 1px solid var(--border); background: var(--surface);
`;

const StatPill = styled.div`
  display: flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 20px; font-size: 11px; font-weight: 600;
  background: var(--card-bg); border: 1px solid var(--card-border);
  color: var(--text-secondary);
  span { font-weight: 800; color: var(--text-primary); }
`;

const TableWrap = styled.div`
  padding: 0 0 24px;
`;

const Table = styled.table`
  width: 100%; border-collapse: separate; border-spacing: 0;
  margin-top: 4px; table-layout: fixed;
  th { position: sticky; top: var(--pin-offset, 0px); background: var(--surface); z-index: 5;
    padding: 6px 6px; text-align: left; font-size: 9px;
    font-weight: 700; color: var(--text-muted); text-transform: uppercase;
    letter-spacing: .4px; border-bottom: 2px solid var(--border);
    white-space: nowrap; cursor: pointer; user-select: none;
    overflow: hidden; text-overflow: ellipsis;
    &:hover { color: var(--primary); }
  }
  thead tr:nth-child(2) th { top: calc(var(--pin-offset, 0px) + 28px); z-index: 4; }
  td { padding: 5px 6px; font-size: 11px; color: var(--text-secondary);
    border-bottom: 1px solid var(--border); vertical-align: middle;
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }
  tr:hover td { background: var(--surface-hover); }
  /* Responsive: hide low-priority columns first */
  .col-hide-1 { /* address, sub-cat, comment, source */ }
  .col-hide-2 { /* followers, commercial, MAJ */ }
  @media (max-width: 1400px) {
    .col-hide-1 { display: none; }
  }
  @media (max-width: 1100px) {
    .col-hide-2 { display: none; }
  }
`;

const InlineStatusSelect = styled.select`
  padding: 4px 8px; border-radius: 10px; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .3px; cursor: pointer;
  border: 1px solid transparent; outline: none; appearance: none;
  -webkit-appearance: none; -moz-appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat; background-position: right 4px center; background-size: 10px;
  padding-right: 18px;
  background-color: ${p => {
    const m = { en_attente: 'rgba(107,114,128,.1)', premier_contact: 'rgba(59,130,246,.15)',
      relance: 'rgba(245,158,11,.15)', mail_envoye: 'rgba(236,72,153,.1)',
      dm_envoye: 'rgba(99,102,241,.1)', msg_envoye: 'rgba(139,92,246,.1)',
      valide: 'rgba(34,197,94,.2)', refuse: 'rgba(239,68,68,.15)',
      annule: 'rgba(239,68,68,.1)' };
    return m[p.$s] || 'var(--surface)';
  }};
  color: ${p => {
    const m = { en_attente: '#6b7280', premier_contact: '#3b82f6',
      relance: '#f59e0b', mail_envoye: '#ec4899',
      dm_envoye: '#6366f1', msg_envoye: '#8b5cf6',
      valide: '#16a34a', refuse: '#ef4444', annule: '#dc2626' };
    return m[p.$s] || 'var(--text-muted)';
  }};
  transition: all .15s;
  &:hover { border-color: var(--primary); }
  &:focus { border-color: var(--primary); box-shadow: 0 0 0 2px rgba(165,145,239,.15); }
`;

const CategoryBadge = styled.span`
  display: inline-flex; align-items: center; gap: 3px;
  padding: 2px 8px; border-radius: 4px; font-size: 9px; font-weight: 700;
  background: ${p => p.$bg || 'rgba(165,145,239,.1)'};
  color: ${p => p.$color || 'var(--primary)'};
  white-space: nowrap; max-width: 120px; overflow: hidden; text-overflow: ellipsis;
`;

const InstaLink = styled.a`
  color: var(--primary); font-weight: 600; text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const ActionBtns = styled.div`
  display: flex; gap: 2px; flex-wrap: nowrap;
  button { background: none; border: none; cursor: pointer; padding: 3px;
    color: var(--text-muted); border-radius: 4px; transition: all .1s;
    &:hover { color: var(--primary); background: var(--surface); }
  }
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; padding: 60px 20px; color: var(--text-muted);
  svg { opacity: .15; margin-bottom: 16px; }
  h3 { font-size: 16px; margin: 0 0 6px; color: var(--text-secondary); }
  p { font-size: 12px; margin: 0; }
`;

const Pagination = styled.div`
  display: flex; align-items: center; justify-content: center; gap: 8px;
  padding: 16px;
  button { padding: 6px 12px; border-radius: 6px; border: 1px solid var(--border);
    background: var(--card-bg); color: var(--text-secondary); font-size: 11px;
    font-weight: 600; cursor: pointer;
    &:disabled { opacity: .3; cursor: not-allowed; }
    &:hover:not(:disabled) { border-color: var(--primary); }
  }
  span { font-size: 12px; color: var(--text-muted); }
`;

/* Modal overlay for add/edit */
const ModalOverlay = styled.div`
  position: fixed; top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,.4); z-index: 2000;
  display: flex; align-items: center; justify-content: center;
`;

const ModalBox = styled.div`
  background: var(--card-bg); border-radius: 16px;
  width: 600px; max-height: 80vh; overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0,0,0,.3);
`;

const ModalHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px; border-bottom: 1px solid var(--border);
  h2 { font-size: 16px; font-weight: 700; color: var(--text-primary); margin: 0; }
  button { background: none; border: none; cursor: pointer; color: var(--text-muted);
    padding: 4px; border-radius: 4px; &:hover { color: var(--error); } }
`;

const ModalBody = styled.div`
  padding: 20px 24px; display: grid;
  grid-template-columns: 1fr 1fr; gap: 12px;
`;

const FormField = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  ${p => p.$full && 'grid-column: 1 / -1;'}
  label { font-size: 10px; font-weight: 700; color: var(--text-muted);
    text-transform: uppercase; letter-spacing: .3px; }
  input, select, textarea { padding: 8px 10px; border-radius: 6px;
    border: 1px solid var(--border); background: var(--input-bg);
    color: var(--text-primary); font-size: 12px;
    &:focus { border-color: var(--primary); outline: none; }
  }
  textarea { resize: vertical; min-height: 60px; }
`;

const ModalFooter = styled.div`
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 14px 24px; border-top: 1px solid var(--border);
`;

const FilterInput = styled.input`
  width: 100%; padding: 4px 6px; border-radius: 4px;
  border: 1px solid var(--border); background: var(--input-bg);
  color: var(--text-primary); font-size: 10px; box-sizing: border-box;
  &:focus { border-color: var(--primary); outline: none; }
  &::placeholder { color: var(--text-muted); font-size: 9px; }
`;

const FilterTh = styled.th`
  && { padding: 3px 4px; border-bottom: 2px solid var(--primary);
    background: var(--surface); cursor: default;
    user-select: auto; overflow: visible; white-space: normal;
    top: calc(var(--pin-offset, 0px) + 28px) !important; z-index: 4 !important; }
`;

const DropWrap = styled.div`
  position: relative; width: 100%;
`;
const DropBtn = styled.div`
  width: 100%; padding: 3px 6px; border-radius: 4px;
  border: 1px solid var(--border); background: var(--input-bg);
  color: ${p => p.$hasValue ? 'var(--text-primary)' : 'var(--text-muted)'};
  font-size: 9px; cursor: pointer; box-sizing: border-box;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  display: flex; align-items: center; justify-content: space-between; gap: 2px;
  &:hover { border-color: var(--primary); }
`;
const DropMenu = styled.div`
  position: fixed; z-index: 9999;
  background: var(--card-bg); border: 1px solid var(--primary);
  border-radius: 6px; box-shadow: 0 8px 24px rgba(0,0,0,.25);
  max-height: 220px; overflow-y: auto;
  min-width: 140px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;
const DropItem = styled.label`
  display: flex; align-items: center; gap: 6px; padding: 5px 8px;
  font-size: 10px; color: var(--text-secondary); cursor: pointer;
  &:hover { background: var(--surface-hover); }
  input { accent-color: var(--primary); width: 13px; height: 13px; cursor: pointer; }
`;

const CheckboxDropdown = React.memo(({ options, selected, onChange, labels, placeholder }) => {
  const [open, setOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState({ top: 0, left: 0, width: 0 });
  const wrapRef = React.useRef(null);
  const btnRef = React.useRef(null);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target) &&
          menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setMenuPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 140) });
    }
    setOpen(!open);
  };

  const toggle = (val) => {
    const set = new Set(selected);
    set.has(val) ? set.delete(val) : set.add(val);
    onChange([...set]);
  };

  const display = selected.length === 0 ? placeholder
    : selected.length === 1 ? (labels?.[selected[0]] || selected[0])
    : `${selected.length} sélectionnés`;

  return (
    <DropWrap ref={wrapRef}>
      <DropBtn ref={btnRef} $hasValue={selected.length > 0} onClick={handleOpen}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{display}</span>
        <ChevronDown size={10} style={{ flexShrink: 0, opacity: .5 }} />
      </DropBtn>
      {open && (
        <DropMenu ref={menuRef} style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width }}>
          {selected.length > 0 && (
            <DropItem style={{ borderBottom: '1px solid var(--border)', color: 'var(--primary)', fontWeight: 700 }}
              onClick={() => { onChange([]); }}>
              <X size={10} /> Tout décocher
            </DropItem>
          )}
          {options.map(o => (
            <DropItem key={o}>
              <input type="checkbox" checked={selected.includes(o)} onChange={() => toggle(o)} />
              {labels?.[o] || o}
            </DropItem>
          ))}
        </DropMenu>
      )}
    </DropWrap>
  );
});

const CellWrap = styled.div`
  cursor: text; min-height: 20px; min-width: 30px;
  padding: 1px 0; border-radius: 3px; transition: background .1s;
  &:hover { background: rgba(165,145,239,.06); }
`;

const CellInput = styled.input`
  width: 100%; padding: 3px 5px; border: 1.5px solid var(--primary);
  border-radius: 4px; background: var(--input-bg); color: var(--text-primary);
  font-size: 11px; box-sizing: border-box; outline: none;
  box-shadow: 0 0 0 2px rgba(165,145,239,.15);
`;

const CellSelect = styled.select`
  width: 100%; padding: 3px 5px; border: 1.5px solid var(--primary);
  border-radius: 4px; background: var(--input-bg); color: var(--text-primary);
  font-size: 11px; box-sizing: border-box; outline: none;
  box-shadow: 0 0 0 2px rgba(165,145,239,.15);
`;

/* ── Inline Editable Cell Component ── */
const EditableCell = React.memo(({ value, field, rowId, type, options, optionLabels, onSave, render }) => {
  const [editing, setEditing] = React.useState(false);
  const [val, setVal] = React.useState(value ?? '');
  const ref = React.useRef(null);

  React.useEffect(() => { setVal(value ?? ''); }, [value]);
  React.useEffect(() => { if (editing && ref.current) ref.current.focus(); }, [editing]);

  const save = () => {
    setEditing(false);
    const cleaned = type === 'number' ? (val === '' ? null : Number(val)) : (val || null);
    if (cleaned !== value && !(cleaned === null && (value === null || value === undefined || value === ''))) {
      onSave(rowId, field, cleaned);
    }
  };

  if (editing) {
    if (options) {
      return (
        <CellSelect ref={ref} value={val} onChange={e => setVal(e.target.value)} onBlur={save}>
          <option value="">—</option>
          {options.map(o => <option key={o} value={o}>{optionLabels?.[o] || o}</option>)}
        </CellSelect>
      );
    }
    return (
      <CellInput ref={ref} type={type || 'text'} value={val}
        onChange={e => setVal(e.target.value)} onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') { setVal(value ?? ''); setEditing(false); } }}
      />
    );
  }

  return (
    <CellWrap onClick={() => { setVal(value ?? ''); setEditing(true); }}>
      {render ? render(value) : (value ?? '—')}
    </CellWrap>
  );
});

/* ═══════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════ */

const STATUSES = ['en_attente', 'premier_contact', 'relance', 'mail_envoye', 'dm_envoye', 'msg_envoye', 'valide', 'refuse', 'annule'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const SOURCES = ['inbound', 'outbound', 'referral', 'physical', 'event', 'website', 'other'];
const STATUS_LABELS = {
  en_attente: 'En attente', premier_contact: '1er contact fait', relance: 'Relancé',
  mail_envoye: 'Mail envoyé', dm_envoye: 'DM envoyé', msg_envoye: 'MSG envoyé',
  valide: 'Validé', refuse: 'Refusé', annule: 'Annulé'
};

const CATEGORIES = [
  'MODE & BEAUTÉ', 'FOOD & BOISSONS', 'VOYAGE & LIFESTYLE', 'FITNESS & SPORT',
  'TECH & DIGITAL', 'CRÉATIF & ARTS', 'MAISON & DÉCO', 'FAMILLE & PARENTALITÉ',
  'ANIMAUX', 'BUSINESS & FINANCE', 'ÉDUCATION & SAVOIR', 'AUTOMOBILE',
  'SANTÉ & BIEN-ÊTRE', 'ÉVÉNEMENTIEL', 'IMMOBILIER', 'SERVICES PRO',
  'TATOUAGE & BODY ART', 'INFLUENCEUR & CONTENU', 'CBD'
];

const CATEGORY_COLORS = {
  'MODE & BEAUTÉ': '#ec4899', 'FOOD & BOISSONS': '#f59e0b',
  'VOYAGE & LIFESTYLE': '#06b6d4', 'FITNESS & SPORT': '#64748b',
  'TECH & DIGITAL': '#22c55e', 'CRÉATIF & ARTS': '#ef4444',
  'MAISON & DÉCO': '#a855f7', 'FAMILLE & PARENTALITÉ': '#8b5cf6',
  'ANIMAUX': '#f97316', 'BUSINESS & FINANCE': '#3b82f6',
  'ÉDUCATION & SAVOIR': '#14b8a6', 'AUTOMOBILE': '#6366f1',
  'SANTÉ & BIEN-ÊTRE': '#10b981', 'ÉVÉNEMENTIEL': '#e11d48',
  'IMMOBILIER': '#0ea5e9', 'SERVICES PRO': '#475569',
  'TATOUAGE & BODY ART': '#1e1b4b', 'INFLUENCEUR & CONTENU': '#d946ef',
  'CBD': '#65a30d'
};

const STATUS_ROW_BG = {
  en_attente: 'rgba(107,114,128,.04)', premier_contact: 'rgba(59,130,246,.06)',
  relance: 'rgba(245,158,11,.06)', mail_envoye: 'rgba(236,72,153,.05)',
  dm_envoye: 'rgba(99,102,241,.05)', msg_envoye: 'rgba(139,92,246,.05)',
  valide: 'rgba(34,197,94,.08)', refuse: 'rgba(239,68,68,.06)',
  annule: 'rgba(239,68,68,.04)'
};

const SALES_COLORS = [
  '#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6',
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#14b8a6',
  '#e11d48', '#65a30d', '#0ea5e9', '#d946ef', '#475569'
];

const getSalesColor = (name) => {
  if (!name) return null;
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return SALES_COLORS[Math.abs(hash) % SALES_COLORS.length];
};

const openInExternalBrowser = (url) => {
  try {
    const { shell } = window.require('electron');
    shell.openExternal(url);
  } catch {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const EMPTY_FORM = {
  insta_username: '', linkedin_name: '', linkedin_url: '', sales_account: '',
  status: 'en_attente', priority: 'medium', posts: '', followers: '', following: '',
  first_message_sent: '', category: '', categories: '', city: '', address: '',
  phone: '', email: '', website: '', comment: '', source: 'outbound',
  source_detail: '', company_name: '', budget_range: '', service_interest: '',
  deal_value: '', language: '', country: ''
};

// Google Sheets → prospects column mapping (case-insensitive keys matched at runtime)
const statusTransform = (v) => {
  if (!v) return 'en_attente';
  const lower = v.toLowerCase().trim();
  const map = {
    'en attente': 'en_attente', 'nouveau': 'en_attente', 'new': 'en_attente',
    '1er contact fait': 'premier_contact', '1er contact': 'premier_contact', 'contacté': 'premier_contact', 'contacted': 'premier_contact',
    'relancé': 'relance', 'relance': 'relance',
    'mail envoyé': 'mail_envoye', 'mail envoye': 'mail_envoye',
    'dm envoyé': 'dm_envoye', 'dm envoye': 'dm_envoye',
    'msg envoyé': 'msg_envoye', 'msg envoye': 'msg_envoye',
    'validé': 'valide', 'valide': 'valide', 'won': 'valide', 'gagné': 'valide', 'done': 'valide',
    'refusé': 'refuse', 'refuse': 'refuse', 'lost': 'refuse', 'perdu': 'refuse',
    'annulé': 'annule', 'annule': 'annule', 'cancelled': 'annule'
  };
  return map[lower] || 'en_attente';
};

const usernameTransform = (v) => {
  if (!v) return null;
  const trimmed = v.trim();
  if (!trimmed || trimmed === '—' || trimmed === '-' || trimmed === '–') return null;
  const match = trimmed.match(/instagram\.com\/([^/?#]+)/i);
  if (match) return match[1].replace('@', '');
  const cleaned = trimmed.replace('@', '').trim();
  return cleaned || null;
};

const intTransform = (v) => v ? parseInt(String(v).replace(/[^\d]/g, '')) || null : null;

const CSV_COLUMN_MAP = {
  // Combined header formats
  'owner media link': { field: 'insta_username', transform: usernameTransform },
  // Individual column formats (actual CSV structure)
  'link': { field: 'insta_username', transform: usernameTransform },
  'lead': { field: 'insta_username', transform: usernameTransform },
  'username': { field: 'insta_username', transform: usernameTransform },
  '@sales account': { field: 'sales_account' },
  'sales account': { field: 'sales_account' },
  // Status columns
  'status lead': { field: 'status', transform: statusTransform },
  'status': { field: 'status', transform: statusTransform },
  // Numeric stats
  'posts': { field: 'posts', transform: intTransform },
  'followers': { field: 'followers', transform: intTransform },
  'followings': { field: 'following', transform: intTransform },
  'following': { field: 'following', transform: intTransform },
  // Message / bio
  'bio to send gen': { field: 'first_message_sent' },
  'to send': { field: 'first_message_sent' },
  'bio': { field: 'insta_bio' },
  'biography': { field: 'insta_bio' },
  'biographie': { field: 'insta_bio' },
  'insta_bio': { field: 'insta_bio' },
  'description': { field: 'insta_bio' },
  'full name': { field: 'insta_full_name' },
  'full_name': { field: 'insta_full_name' },
  'nom complet': { field: 'insta_full_name' },
  'nom': { field: 'insta_full_name' },
  'name': { field: 'insta_full_name' },
  'categorie': { field: 'category' },
  'category': { field: 'category' },
  'sous categorie': { field: 'categories' },
  'sous-categorie': { field: 'categories' },
  'sub category': { field: 'categories' },
  'subcategory': { field: 'categories' },
  'commentaire': { field: 'comment' },
  'comment': { field: 'comment' },
  'notes': { field: 'comment' },
  'place': { field: 'city' },
  'ville': { field: 'city' },
  'city': { field: 'city' },
  'adresse': { field: 'address' },
  'address': { field: 'address' },
  'tel': { field: 'phone' },
  'phone': { field: 'phone' },
  'téléphone': { field: 'phone' },
  'telephone': { field: 'phone' },
  'numero': { field: 'phone' },
  'mail': { field: 'email' },
  'email': { field: 'email' },
  'e-mail': { field: 'email' },
  'website': { field: 'website' },
  'site': { field: 'website' },
  'site web': { field: 'website' },
  'url': { field: 'website' },
  'date contact': { field: 'first_contact_date' },
  'last date': { field: 'last_status_update' },
  'source': { field: 'source' },
  'country': { field: 'country' },
  'pays': { field: 'country' },
  'region': { field: 'region' },
  'région': { field: 'region' },
  'langue': { field: 'language' },
  'language': { field: 'language' },
  'linkedin': { field: 'linkedin_url' },
  'linkedin url': { field: 'linkedin_url' },
  'linkedin name': { field: 'linkedin_name' },
  'entreprise': { field: 'company_name' },
  'company': { field: 'company_name' },
  'company name': { field: 'company_name' },
  'société': { field: 'company_name' },
  'budget': { field: 'budget_range' },
  'deal': { field: 'deal_value', transform: (v) => v ? parseFloat(String(v).replace(/[^\d.,]/g, '').replace(',', '.')) || null : null },
  'valeur': { field: 'deal_value', transform: (v) => v ? parseFloat(String(v).replace(/[^\d.,]/g, '').replace(',', '.')) || null : null },
};

const ViewToggleGroup = styled.div`
  display: flex; border: 1px solid var(--border); border-radius: 8px; overflow: hidden;
`;
const ViewToggleBtn = styled.button`
  display: flex; align-items: center; justify-content: center; gap: 4px;
  padding: 6px 10px; border: none; font-size: 10px; font-weight: 700; cursor: pointer;
  background: ${p => p.$active ? 'var(--primary)' : 'var(--card-bg)'};
  color: ${p => p.$active ? '#fff' : 'var(--text-muted)'};
  transition: all .15s;
  &:hover { opacity: .85; }
`;

const CardList = styled.div`
  display: flex; flex-direction: column; gap: 6px; padding: 12px 16px;
`;

const ProspectCard = styled.div`
  display: flex; gap: 14px; padding: 14px 16px;
  background: ${p => p.$bg || 'var(--card-bg)'}; border: 1px solid var(--card-border);
  border-radius: 12px; transition: all .15s; position: relative;
  ${p => p.$selected && 'border-color: var(--primary); box-shadow: 0 0 0 2px rgba(165,145,239,.15);'}
  &:hover { border-color: var(--primary); }
`;

const CardAvatar = styled.div`
  flex-shrink: 0; width: 56px; height: 56px; border-radius: 14px;
  overflow: hidden; border: 2px solid var(--border); background: var(--surface);
  display: flex; align-items: center; justify-content: center;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const CardAvatarFallback = styled.div`
  width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, var(--primary), #8b5cf6);
  color: #fff; font-size: 20px; font-weight: 800;
`;

const CardBody = styled.div`
  flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 6px;
`;

const CardBio = styled.div`
  font-size: 11px; color: var(--text-secondary); line-height: 1.5;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical;
  overflow: hidden; max-height: 34px;
`;

const CardStatsRow = styled.div`
  display: flex; gap: 12px; align-items: center;
`;

const CardStat = styled.span`
  display: flex; align-items: center; gap: 3px;
  font-size: 10px; font-weight: 700; color: var(--text-muted);
  span { color: var(--text-primary); }
`;

const CardMsgArea = styled.div`
  margin-top: 6px; padding: 8px 10px; border-radius: 8px;
  background: var(--surface); border: 1px solid var(--border);
  font-size: 11px; color: var(--text-secondary); line-height: 1.5;
  max-height: 60px; overflow-y: auto;
  &:empty::before {
    content: 'Aucun message généré'; color: var(--text-muted); font-style: italic;
  }
`;

const ImportDropZone = styled.div`
  border: 2px dashed ${p => p.$active ? 'var(--primary)' : 'var(--border)'};
  border-radius: 12px; padding: 32px; text-align: center; cursor: pointer;
  transition: all .2s; background: ${p => p.$active ? 'rgba(165,145,239,.06)' : 'transparent'};
  &:hover { border-color: var(--primary); background: rgba(165,145,239,.04); }
  svg { color: var(--text-muted); margin-bottom: 8px; }
  p { margin: 4px 0; font-size: 12px; color: var(--text-muted); }
  strong { color: var(--text-primary); font-size: 14px; }
`;

const MappingTable = styled.table`
  width: 100%; border-collapse: collapse; margin-top: 12px;
  th { padding: 6px 10px; text-align: left; font-size: 10px; font-weight: 700;
    color: var(--text-muted); text-transform: uppercase; border-bottom: 1px solid var(--border); }
  td { padding: 6px 10px; font-size: 12px; color: var(--text-secondary);
    border-bottom: 1px solid var(--border); }
  tr:hover td { background: var(--surface-hover); }
`;

const MappedBadge = styled.span`
  display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600;
  background: ${p => p.$mapped ? 'rgba(34,197,94,.1)' : 'rgba(239,68,68,.1)'};
  color: ${p => p.$mapped ? '#22c55e' : '#ef4444'};
`;

const PreviewScroll = styled.div`
  max-height: 200px; overflow-y: auto; margin-top: 12px;
  border: 1px solid var(--border); border-radius: 8px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: var(--border); border-radius: 2px; }
`;

const ImportStats = styled.div`
  display: flex; gap: 16px; padding: 12px 0; font-size: 13px; color: var(--text-secondary);
  span { font-weight: 700; color: var(--text-primary); }
`;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const CrmProspects = () => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [filterSources, setFilterSources] = useState([]);
  const [filterSales, setFilterSales] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubCategory, setFilterSubCategory] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterAddress, setFilterAddress] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterPhone, setFilterPhone] = useState('');
  const [filterComment] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [salesAccounts, setSalesAccounts] = useState([]);
  const [salesCounts, setSalesCounts] = useState({});
  const [cities, setCities] = useState([]);
  const [actionModal, setActionModal] = useState(null); // legacy — kept for compatibility
  const [actionForm, setActionForm] = useState({});
  // Inline communication panel
  const [expandedPanel, setExpandedPanel] = useState(null); // { prospectId, tab: 'gmail'|'calendar' }
  const [panelForm, setPanelForm] = useState({ subject: '', body: '', summary: '', date: '', time: '10:00', channel: 'email' });
  // Mass selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [massEnriching, setMassEnriching] = useState(false);
  const [viewMode, setViewMode] = useState('compact'); // 'compact' | 'expanded'
  // Sticky pin sections
  const { sectionRef, getStickyStyle, totalPinnedHeight } = useStickyPin('crm', ['toolbar', 'stats']);
  // Owner stats
  const [ownerStats, setOwnerStats] = useState([]);
  const [expandedOwner, setExpandedOwner] = useState(null); // 'Quentin' | 'Lucas' | null

  // Owner mapping: Lucas labels explicitly listed, Quentin = default
  const LUCAS_LABELS = ['MTG Physique', 'Lucas Montagner'];
  const getOwner = (salesAccount) => {
    if (!salesAccount) return 'Quentin';
    return LUCAS_LABELS.includes(salesAccount) ? 'Lucas' : 'Quentin';
  };
  const OWNER_EMAILS = { Quentin: 'qjennes@gmail.com', Lucas: 'lucas.montagner@myicn.fr' };
  const OWNER_COLORS = { Quentin: '#a591ef', Lucas: '#10b981' };

  const limit = 50;

  const fetchProspects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit, sort: sortBy, dir: sortDir });
      if (search) params.set('search', search);
      if (filterStatuses.length) params.set('status', filterStatuses.join(','));
      if (filterSources.length) params.set('source', filterSources.join(','));
      if (filterSales) params.set('sales_account', filterSales);
      if (filterCategory) params.set('category', filterCategory);
      if (filterSubCategory) params.set('sub_category', filterSubCategory);
      if (filterCity) params.set('city', filterCity);
      if (filterAddress) params.set('address', filterAddress);
      if (filterEmail) params.set('email', filterEmail);
      if (filterPhone) params.set('phone', filterPhone);
      if (filterComment) params.set('comment', filterComment);
      const r = await axios.get(`/api/prospects?${params}`);
      setProspects(r.data.prospects || []);
      setTotal(r.data.total || 0);
      setStats(r.data.stats || {});
      setSalesAccounts(r.data.salesAccounts || []);
      setSalesCounts(r.data.salesCounts || {});
      setCities(r.data.cities || []);
      setOwnerStats(r.data.ownerStats || []);
    } catch (e) { console.error('CRM fetch error:', e); }
    setLoading(false);
  }, [page, limit, sortBy, sortDir, search, filterStatuses, filterSources, filterSales, filterCategory, filterSubCategory, filterCity, filterAddress, filterEmail, filterPhone, filterComment]);

  useEffect(() => { fetchProspects(); }, [fetchProspects]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  // ── Mass selection helpers ──
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = prospects.length > 0 && prospects.every(p => selectedIds.has(p.id));
  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(prospects.map(p => p.id)));
  };

  const massEnrich = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Enrichir ${ids.length} prospect(s) avec Gemini + Google Search ?`)) return;
    setMassEnriching(true);
    try {
      const r = await axios.post('/api/prospects/enrich-selected', { ids });
      alert(`✅ ${r.data.enriched} enrichi(s), ${r.data.failed} échoué(s)`);
      setSelectedIds(new Set());
      fetchProspects();
    } catch (e) { alert('Erreur: ' + (e.response?.data?.error || e.message)); }
    setMassEnriching(false);
  };

  const massDelete = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`⚠️ Supprimer ${ids.length} prospect(s) ? Cette action est irréversible.`)) return;
    try {
      await Promise.all(ids.map(id => axios.delete(`/api/prospects/${id}`)));
      setSelectedIds(new Set());
      fetchProspects();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const massStatusChange = async (newStatus) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    try {
      await Promise.all(ids.map(id => axios.put(`/api/prospects/${id}`, { status: newStatus })));
      setSelectedIds(new Set());
      fetchProspects();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const openAdd = () => {
    setForm({ ...EMPTY_FORM });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEdit = (p) => {
    setForm({
      insta_username: p.insta_username || '',
      linkedin_name: p.linkedin_name || '',
      linkedin_url: p.linkedin_url || '',
      sales_account: p.sales_account || '',
      status: p.status || 'new',
      priority: p.priority || 'medium',
      posts: p.posts ?? '',
      followers: p.followers ?? '',
      following: p.following ?? '',
      first_message_sent: p.first_message_sent || '',
      category: p.category || '',
      categories: p.categories || '',
      city: p.city || '',
      address: p.address || '',
      phone: p.phone || '',
      email: p.email || '',
      website: p.website || '',
      comment: p.comment || '',
      source: p.source || 'outbound',
      source_detail: p.source_detail || '',
      company_name: p.company_name || '',
      budget_range: p.budget_range || '',
      service_interest: p.service_interest || '',
      deal_value: p.deal_value ?? '',
      language: p.language || '',
      country: p.country || ''
    });
    setEditingId(p.id);
    setModalOpen(true);
  };

  const saveProspect = async () => {
    try {
      if (editingId) {
        await axios.put(`/api/prospects/${editingId}`, form);
      } else {
        await axios.post('/api/prospects', form);
      }
      setModalOpen(false);
      fetchProspects();
    } catch (e) { console.error('CRM save error:', e); }
  };

  const deleteProspect = async (id) => {
    if (!window.confirm('Supprimer ce prospect ?')) return;
    try {
      await axios.delete(`/api/prospects/${id}`);
      fetchProspects();
    } catch (e) { console.error('CRM delete error:', e); }
  };

  const updateStatus = async (id, newStatus) => {
    setProspects(prev => prev.map(p => p.id === id ? { ...p, status: newStatus } : p));
    try {
      await axios.put(`/api/prospects/${id}`, { status: newStatus, last_status_update: new Date().toISOString() });
    } catch (e) {
      console.error('Status update error:', e);
      fetchProspects();
    }
  };

  // ── Pending changes system (batch inline edits) ──
  const [pendingChanges, setPendingChanges] = useState({}); // { 'id::field': { id, field, value } }

  const updateField = (id, field, value) => {
    setProspects(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
    setPendingChanges(prev => ({ ...prev, [`${id}::${field}`]: { id, field, value } }));
  };

  const pendingCount = Object.keys(pendingChanges).length;

  const savePendingChanges = async () => {
    const grouped = {};
    Object.values(pendingChanges).forEach(({ id, field, value }) => {
      if (!grouped[id]) grouped[id] = {};
      grouped[id][field] = value;
    });
    try {
      await Promise.all(Object.entries(grouped).map(([id, data]) => axios.put(`/api/prospects/${id}`, data)));
      setPendingChanges({});
      fetchProspects();
    } catch (e) {
      console.error('Batch save error:', e);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const discardPendingChanges = () => {
    setPendingChanges({});
    fetchProspects();
  };

  const up = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const totalPages = Math.ceil(total / limit) || 1;

  // ── CSV Import state ──
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [csvMapping, setCsvMapping] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = React.useRef(null);

  const parseCSV = (text) => {
    // Detect delimiter: tab or comma or semicolon
    const firstLine = text.split('\n')[0];
    let delim = ',';
    if (firstLine.split('\t').length > firstLine.split(',').length) delim = '\t';
    else if (firstLine.split(';').length > firstLine.split(',').length) delim = ';';

    const lines = text.split('\n').filter(l => l.trim());
    if (lines.length < 2) return { headers: [], rows: [] };

    const parseRow = (line) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuotes = !inQuotes; continue; }
        if (ch === delim && !inQuotes) { result.push(current.trim()); current = ''; continue; }
        current += ch;
      }
      result.push(current.trim());
      return result;
    };

    const headers = parseRow(lines[0]);
    const rows = lines.slice(1).map(l => parseRow(l)).filter(r => r.some(c => c));
    return { headers, rows };
  };

  const buildMapping = (headers) => {
    return headers.map(h => {
      const key = h.toLowerCase().trim();
      const mapped = CSV_COLUMN_MAP[key];
      return { csvHeader: h, prospectField: mapped ? mapped.field : null, transform: mapped ? mapped.transform : null };
    });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target.result;
      const { headers, rows } = parseCSV(text);
      setCsvHeaders(headers);
      setCsvRows(rows);
      setCsvMapping(buildMapping(headers));
      setImportResult(null);
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleCsvImport = async () => {
    if (csvRows.length === 0) return;
    setImporting(true);
    try {
      const profiles = csvRows.map(row => {
        const obj = {};
        csvMapping.forEach((m, i) => {
          if (m.prospectField && row[i] !== undefined) {
            const val = row[i] || '';
            obj[m.prospectField] = m.transform ? m.transform(val) : (val || null);
          }
        });
        // Ensure insta_username exists for dedup
        if (!obj.insta_username) obj.insta_username = null;
        return obj;
      }).filter(p => p.insta_username);

      const resp = await axios.post('/api/prospects/bulk', {
        profiles,
        source: 'outbound'
      });
      setImportResult(resp.data);
      fetchProspects();
    } catch (e) {
      console.error('CSV import error:', e);
      const serverMsg = e.response?.data?.error;
      setImportResult({ error: serverMsg || e.message });
    }
    setImporting(false);
  };

  const openImportModal = () => {
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvMapping([]);
    setImportResult(null);
    setImportModalOpen(true);
  };

  const openPanel = (prospectId, tab) => {
    const p = prospects.find(pr => pr.id === prospectId);
    if (expandedPanel?.prospectId === prospectId && expandedPanel?.tab === tab) {
      setExpandedPanel(null);
      return;
    }
    setPanelForm({
      subject: `Prise de contact - ${p?.company_name || p?.insta_username || ''}`,
      body: `Bonjour${p?.insta_full_name ? ' ' + p.insta_full_name : ''},\n\n\n\nCordialement`,
      summary: `RDV - ${p?.company_name || p?.insta_username || ''}`,
      date: new Date().toISOString().split('T')[0],
      time: '10:00',
      channel: tab === 'gmail' ? 'email' : 'calendar',
    });
    setExpandedPanel({ prospectId, tab });
  };

  const [generatingEmail, setGeneratingEmail] = useState(false);

  const generateEmailAI = async (p) => {
    setGeneratingEmail(true);
    try {
      const r = await axios.post('/api/google/gmail/generate-email', { prospect_id: p.id });
      if (r.data.success) {
        setPanelForm(f => ({ ...f, subject: r.data.subject, body: r.data.body }));
      } else { alert(r.data.error || 'Erreur génération'); }
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setGeneratingEmail(false);
  };

  const sendGmail = async (p) => {
    try {
      const r = await axios.post('/api/google/gmail/send', { to: p.email, subject: panelForm.subject, body: panelForm.body, prospect_id: p.id });
      if (r.data.success) { setExpandedPanel(null); alert('Email envoyé'); } else alert(r.data.error || 'Erreur');
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const createCalendarEvent = async (p) => {
    const start = `${panelForm.date}T${panelForm.time}:00`;
    const h = parseInt(panelForm.time.split(':')[0]) + 1;
    const end = `${panelForm.date}T${String(h).padStart(2,'0')}:${panelForm.time.split(':')[1]}:00`;
    try {
      const r = await axios.post('/api/google/calendar/event', {
        summary: panelForm.summary, start, end, prospect_id: p.id,
        location: p.address || p.city || '',
        attendees: p.email ? [p.email] : [],
        description: `Prospect: ${p.insta_username || ''}\n${p.company_name || ''}\n${p.phone || ''}`
      });
      if (r.data.success) { setExpandedPanel(null); alert('RDV créé'); } else alert(r.data.error || 'Erreur');
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const renderCommPanel = (p) => {
    if (!expandedPanel || expandedPanel.prospectId !== p.id) return null;
    const tab = expandedPanel.tab;
    return (
      <tr>
        <td colSpan={14} style={{ padding: 0, background: 'var(--surface)', borderBottom: '2px solid var(--primary)' }}>
          <div style={{ padding: '12px 16px', animation: 'fadeIn .15s ease' }}>
            {/* Tab switcher + contact channels */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
              <button onClick={() => setExpandedPanel(ep => ({ ...ep, tab: 'gmail' }))}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 6, border: `1.5px solid ${tab === 'gmail' ? 'var(--primary)' : 'var(--border)'}`, background: tab === 'gmail' ? 'rgba(165,145,239,.1)' : 'var(--card-bg)', color: tab === 'gmail' ? 'var(--primary)' : 'var(--text-secondary)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                <Mail size={13} /> Gmail
              </button>
              <button onClick={() => setExpandedPanel(ep => ({ ...ep, tab: 'calendar' }))}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 6, border: `1.5px solid ${tab === 'calendar' ? '#4285f4' : 'var(--border)'}`, background: tab === 'calendar' ? 'rgba(66,133,244,.1)' : 'var(--card-bg)', color: tab === 'calendar' ? '#4285f4' : 'var(--text-secondary)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                <Calendar size={13} /> Calendar
              </button>
              {p.insta_username && (
                <button onClick={() => setExpandedPanel(ep => ({ ...ep, tab: 'profil' }))}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 6, border: `1.5px solid ${tab === 'profil' ? '#e1306c' : 'var(--border)'}`, background: tab === 'profil' ? 'rgba(225,48,108,.1)' : 'var(--card-bg)', color: tab === 'profil' ? '#e1306c' : 'var(--text-secondary)', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                  <Eye size={13} /> Profil
                </button>
              )}
              <div style={{ flex: 1 }} />
              {/* Contact channel badges */}
              <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {p.email && <span style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 4, background: 'rgba(34,197,94,.1)', color: '#22c55e', fontSize: 9, fontWeight: 700 }}><Mail size={9} /> Email</span>}
                {p.phone && <span style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 4, background: 'rgba(59,130,246,.1)', color: '#3b82f6', fontSize: 9, fontWeight: 700 }}><Phone size={9} /> Tel</span>}
                {p.phone && <span style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 4, background: 'rgba(37,211,102,.1)', color: '#25d366', fontSize: 9, fontWeight: 700, cursor: 'pointer' }} onClick={() => window.open(`https://wa.me/${p.phone.replace(/[^0-9+]/g, '')}`, '_blank')}><MessageSquare size={9} /> WhatsApp</span>}
                {p.insta_username && <span style={{ display: 'flex', alignItems: 'center', gap: 3, padding: '3px 8px', borderRadius: 4, background: 'rgba(225,48,108,.1)', color: '#e1306c', fontSize: 9, fontWeight: 700, cursor: 'pointer' }} onClick={() => openInExternalBrowser(`https://instagram.com/${p.insta_username}`)}><Instagram size={9} /> DM</span>}
              </div>
              <button onClick={() => setExpandedPanel(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }}>
                <X size={14} />
              </button>
            </div>

            {tab === 'profil' ? (
              /* ── PROFIL TAB ── */
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                {/* Screenshot */}
                <div style={{ flex: '0 0 auto' }}>
                  {p.screenshot_path ? (
                    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '2px solid var(--border)', maxWidth: 280, cursor: 'pointer' }}
                      onClick={() => window.open(p.screenshot_path, '_blank')}>
                      <img src={p.screenshot_path} alt={`Screenshot @${p.insta_username}`}
                        style={{ width: '100%', display: 'block' }}
                        onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                      <div style={{ display: 'none', width: 280, height: 200, alignItems: 'center', justifyContent: 'center', background: 'var(--card-bg)', color: 'var(--text-muted)', fontSize: 11 }}>
                        Screenshot non disponible
                      </div>
                      <div style={{ position: 'absolute', bottom: 6, right: 6, padding: '3px 8px', borderRadius: 4, background: 'rgba(0,0,0,.6)', color: '#fff', fontSize: 9, fontWeight: 700 }}>
                        Cliquer pour agrandir
                      </div>
                    </div>
                  ) : (
                    <div style={{ width: 280, height: 200, borderRadius: 10, border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: 'var(--text-muted)' }}>
                      <Eye size={24} style={{ opacity: .3 }} />
                      <span style={{ fontSize: 11 }}>Pas de screenshot</span>
                      <span style={{ fontSize: 9, opacity: .6 }}>Sera capturé au prochain scraping</span>
                    </div>
                  )}
                </div>
                {/* Profile info side */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src={getLocalProfilePicUrl(p.insta_username)} alt=""
                      style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--primary)' }}
                      onError={e => { if (!e.target.dataset.fb) { e.target.dataset.fb='1'; e.target.src=`https://unavatar.io/instagram/${p.insta_username}`; } else e.target.style.display='none'; }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{p.insta_full_name || p.insta_username}</div>
                      <div style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 600 }}>@{p.insta_username}</div>
                    </div>
                    <a href={`https://instagram.com/${p.insta_username}`} target="_blank" rel="noopener noreferrer"
                      style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, background: 'rgba(225,48,108,.1)', color: '#e1306c', fontSize: 10, fontWeight: 700, textDecoration: 'none', border: '1px solid rgba(225,48,108,.2)' }}>
                      <ExternalLink size={10} /> Instagram
                    </a>
                  </div>
                  {p.insta_bio && <div style={{ fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.5, padding: '6px 10px', background: 'var(--card-bg)', borderRadius: 6, border: '1px solid var(--border)' }}>{p.insta_bio}</div>}
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    {p.followers != null && <div style={{ fontSize: 11 }}><span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{p.followers?.toLocaleString()}</span> <span style={{ color: 'var(--text-muted)' }}>followers</span></div>}
                    {p.following != null && <div style={{ fontSize: 11 }}><span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{p.following?.toLocaleString()}</span> <span style={{ color: 'var(--text-muted)' }}>following</span></div>}
                    {p.posts != null && <div style={{ fontSize: 11 }}><span style={{ fontWeight: 800, color: 'var(--text-primary)' }}>{p.posts?.toLocaleString()}</span> <span style={{ color: 'var(--text-muted)' }}>posts</span></div>}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {p.is_business_account && <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, background: 'rgba(59,130,246,.1)', color: '#3b82f6', fontWeight: 700, border: '1px solid rgba(59,130,246,.2)' }}>PRO</span>}
                    {p.is_verified && <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, background: 'rgba(59,130,246,.1)', color: '#3b82f6', fontWeight: 700 }}>✓ Vérifié</span>}
                    {p.category && <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, background: 'rgba(165,145,239,.1)', color: 'var(--primary)', fontWeight: 700 }}>{p.category}</span>}
                    {p.language && <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, background: 'rgba(107,114,128,.1)', color: '#6b7280', fontWeight: 700 }}>{p.language}</span>}
                    {p.city && <span style={{ fontSize: 9, padding: '2px 8px', borderRadius: 4, background: 'rgba(34,197,94,.1)', color: '#22c55e', fontWeight: 700 }}><MapPin size={8} style={{ marginRight: 2 }} />{p.city}</span>}
                  </div>
                </div>
              </div>
            ) : tab === 'gmail' ? (
              /* ── GMAIL TAB ── */
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 6 }}>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 700, whiteSpace: 'nowrap' }}>A :</span>
                    <span style={{ fontSize: 11, color: p.email ? 'var(--text-primary)' : 'var(--error)', fontWeight: 600 }}>{p.email || 'Pas d\'email'}</span>
                  </div>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <input value={panelForm.subject} onChange={e => setPanelForm(f => ({ ...f, subject: e.target.value }))}
                    placeholder="Objet du mail..."
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, boxSizing: 'border-box' }} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <textarea value={panelForm.body} onChange={e => setPanelForm(f => ({ ...f, body: e.target.value }))}
                    rows={8} placeholder="Corps du message... Cliquez 'Générer par IA' pour remplir automatiquement"
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }} />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => generateEmailAI(p)} disabled={generatingEmail}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 6, border: '1.5px solid #f59e0b', background: generatingEmail ? 'rgba(245,158,11,.15)' : 'rgba(245,158,11,.08)', color: '#f59e0b', fontSize: 11, fontWeight: 700, cursor: generatingEmail ? 'wait' : 'pointer' }}>
                      <Sparkles size={11} style={generatingEmail ? { animation: 'spin 1s linear infinite' } : {}} /> {generatingEmail ? 'Génération...' : 'Générer par IA'}
                    </button>
                    <button onClick={() => {
                      window.open(`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(p.email || '')}&su=${encodeURIComponent(panelForm.subject)}&body=${encodeURIComponent(panelForm.body)}`, '_blank');
                    }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--card-bg)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                      <ExternalLink size={11} /> Ouvrir dans Gmail
                    </button>
                  </div>
                  <button onClick={() => sendGmail(p)} disabled={!p.email}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', borderRadius: 6, border: 'none', background: p.email ? 'var(--primary)' : 'var(--surface)', color: p.email ? '#fff' : 'var(--text-muted)', fontSize: 11, fontWeight: 700, cursor: p.email ? 'pointer' : 'not-allowed' }}>
                    <Send size={11} /> Envoyer via API
                  </button>
                </div>
              </div>
            ) : (
              /* ── CALENDAR TAB ── */
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <input value={panelForm.summary} onChange={e => setPanelForm(f => ({ ...f, summary: e.target.value }))}
                    placeholder="Titre du RDV..."
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Date</label>
                  <input type="date" value={panelForm.date} onChange={e => setPanelForm(f => ({ ...f, date: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>Heure</label>
                  <input type="time" value={panelForm.time} onChange={e => setPanelForm(f => ({ ...f, time: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-primary)', fontSize: 12, boxSizing: 'border-box' }} />
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 6 }}>
                    <Mail size={10} color="var(--text-muted)" />
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Invité :</span>
                    <span style={{ fontSize: 11, color: p.email ? 'var(--text-primary)' : 'var(--text-muted)', fontWeight: 600 }}>{p.email || 'Pas d\'email'}</span>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 6 }}>
                    <MapPin size={10} color="var(--text-muted)" />
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Lieu :</span>
                    <span style={{ fontSize: 11, color: 'var(--text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.address || p.city || '—'}</span>
                  </div>
                </div>
                {/* Contact method selector */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Canal de contact</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[
                      { id: 'email', icon: Mail, label: 'Email', color: '#22c55e', ok: !!p.email },
                      { id: 'phone', icon: Phone, label: 'Téléphone', color: '#3b82f6', ok: !!p.phone },
                      { id: 'whatsapp', icon: MessageSquare, label: 'WhatsApp', color: '#25d366', ok: !!p.phone },
                      { id: 'instagram', icon: Instagram, label: 'Instagram DM', color: '#e1306c', ok: !!p.insta_username },
                    ].map(ch => (
                      <button key={ch.id} onClick={() => setPanelForm(f => ({ ...f, channel: ch.id }))}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, border: `1.5px solid ${panelForm.channel === ch.id ? ch.color : 'var(--border)'}`, background: panelForm.channel === ch.id ? `${ch.color}12` : 'var(--card-bg)', color: panelForm.channel === ch.id ? ch.color : 'var(--text-muted)', fontSize: 10, fontWeight: 700, cursor: ch.ok ? 'pointer' : 'not-allowed', opacity: ch.ok ? 1 : 0.4 }}
                        disabled={!ch.ok}>
                        <ch.icon size={11} /> {ch.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                  <button onClick={() => createCalendarEvent(p)}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', borderRadius: 6, border: 'none', background: '#4285f4', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                    <Calendar size={11} /> Créer le RDV
                  </button>
                </div>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const SortIcon = ({ col }) => (
    sortBy === col
      ? <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 900, color: 'var(--primary)' }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
      : <ArrowUpDown size={9} style={{ opacity: .25, marginLeft: 4 }} />
  );

  return (
    <Page>
      {/* ── Top Bar ── */}
      <TopBar ref={sectionRef('toolbar')} style={getStickyStyle('toolbar')}>
        <Title><Contact size={20} /> CRM Prospects</Title>
        <Spacer />
        <SearchBar>
          <Search size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <input placeholder="Rechercher par nom, username, ville..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          {search && <X size={12} style={{ cursor: 'pointer', color: 'var(--text-muted)' }}
            onClick={() => setSearch('')} />}
        </SearchBar>
        <ViewToggleGroup>
          <ViewToggleBtn $active={viewMode === 'compact'} onClick={() => setViewMode('compact')} title="Vue compacte">
            <LayoutList size={13} />
          </ViewToggleBtn>
          <ViewToggleBtn $active={viewMode === 'expanded'} onClick={() => setViewMode('expanded')} title="Vue étendue">
            <LayoutGrid size={13} />
          </ViewToggleBtn>
        </ViewToggleGroup>
        <SmallBtn onClick={fetchProspects} disabled={loading}>
          <RefreshCw size={12} className={loading ? 'spin' : ''} /> Actualiser
        </SmallBtn>
        <SmallBtn onClick={openImportModal}>
          <Upload size={12} /> Importer CSV
        </SmallBtn>
        <SmallBtn onClick={async () => {
          if (!window.confirm('Géocoder tous les prospects ayant une adresse/ville ?')) return;
          try {
            const r = await axios.post('/api/prospects/geocode-batch');
            alert(`✅ ${r.data.geocoded} géocodé(s), ${r.data.failed} échoué(s)`);
            fetchProspects();
          } catch (e) { alert('Erreur: ' + e.message); }
        }}>
          <MapPin size={12} /> Géocoder
        </SmallBtn>
        <SmallBtn onClick={async () => {
          if (!window.confirm('Enrichir 20 prospects via Gemini + Google Search ?\n(Sync instagram_profile_visits → enrichissement Gemini)')) return;
          try {
            const r = await axios.post('/api/prospects/enrich-address-batch', { limit: 20 });
            alert(`✅ Enrichissement terminé !\n\n🔄 ${r.data.synced || 0} profils synchronisés\n📍 ${r.data.dbUpdated} prospects enrichis\n❌ ${r.data.failed} échoué(s)\n⏭️ ${r.data.skipped} ignoré(s)`);
            fetchProspects();
          } catch (e) { alert('Erreur: ' + e.message); }
        }} style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#fff' }}>
          <Sparkles size={12} /> Enrichir (Gemini)
        </SmallBtn>
        <SmallBtn $primary onClick={openAdd}>
          <Plus size={12} /> Nouveau prospect
        </SmallBtn>
      </TopBar>

      {/* ── Stats Row ── */}
      <StatsRow ref={sectionRef('stats')} style={{ flexWrap: 'wrap', background: 'var(--background)', ...getStickyStyle('stats') }}>
        <StatPill><Users size={12} /> Total: <span>{total}</span></StatPill>
        <StatPill style={{ color: '#6b7280' }}>En attente: <span>{stats.en_attente || 0}</span></StatPill>
        <StatPill style={{ color: '#3b82f6' }}>1er contact: <span>{stats.premier_contact || 0}</span></StatPill>
        <StatPill style={{ color: '#f59e0b' }}>Relancés: <span>{stats.relance || 0}</span></StatPill>
        <StatPill style={{ color: '#16a34a' }}>Validés: <span>{stats.valide || 0}</span></StatPill>
        <StatPill style={{ color: '#ef4444' }}>Refusés: <span>{stats.refuse || 0}</span></StatPill>
        <StatPill style={{ color: '#dc2626' }}>Annulés: <span>{stats.annule || 0}</span></StatPill>
        <div style={{ width: '1px', height: 20, background: 'var(--border)', margin: '0 4px' }} />
        {/* Owner groups */}
        {(() => {
          const ownerTotals = { Quentin: 0, Lucas: 0 };
          const ownerStatusMap = { Quentin: {}, Lucas: {} };
          ownerStats.forEach(r => {
            const owner = getOwner(r.sales_account);
            ownerTotals[owner] = (ownerTotals[owner] || 0) + r.count;
            ownerStatusMap[owner][r.status] = (ownerStatusMap[owner][r.status] || 0) + r.count;
          });
          return ['Quentin', 'Lucas'].map(owner => (
            <div key={owner} style={{ position: 'relative' }}>
              <StatPill style={{ cursor: 'pointer', borderColor: expandedOwner === owner ? OWNER_COLORS[owner] : undefined, gap: 5 }}
                onClick={() => setExpandedOwner(expandedOwner === owner ? null : owner)}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: OWNER_COLORS[owner], display: 'inline-block', flexShrink: 0 }} />
                {owner}: <span>{ownerTotals[owner] || 0}</span>
                <ChevronRight size={10} style={{ transform: expandedOwner === owner ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
              </StatPill>
              {expandedOwner === owner && (
                <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '8px 12px', boxShadow: '0 8px 24px rgba(0,0,0,.25)', zIndex: 30, minWidth: 200, whiteSpace: 'nowrap' }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: OWNER_COLORS[owner], marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: OWNER_COLORS[owner], display: 'inline-block' }} />
                    {owner} — {OWNER_EMAILS[owner]}
                  </div>
                  {STATUSES.map(s => {
                    const cnt = ownerStatusMap[owner]?.[s] || 0;
                    if (!cnt) return null;
                    return (
                      <div key={s} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, fontSize: 11, padding: '2px 0', color: 'var(--text-secondary)' }}>
                        <span>{STATUS_LABELS[s]}</span>
                        <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{cnt}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ));
        })()}
        <div style={{ width: '1px', height: 20, background: 'var(--border)', margin: '0 4px' }} />
        {salesAccounts.map(sa => {
          const c = getSalesColor(sa);
          return (
            <StatPill key={sa} style={{ cursor: 'pointer', borderColor: filterSales === sa ? c : undefined }}
              onClick={() => { setFilterSales(filterSales === sa ? '' : sa); setPage(1); }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: c, display: 'inline-block', flexShrink: 0 }} />
              {sa}: <span>{salesCounts[sa] || 0}</span>
            </StatPill>
          );
        })}
        {/* Inline compact pagination */}
        {totalPages > 1 && (
          <>
            <div style={{ width: '1px', height: 20, background: 'var(--border)', margin: '0 4px' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 8px', cursor: page <= 1 ? 'not-allowed' : 'pointer', color: page <= 1 ? 'var(--text-muted)' : 'var(--text-secondary)', fontSize: 10, fontWeight: 700, opacity: page <= 1 ? .4 : 1 }}>
                ←
              </button>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                {page}/{totalPages}
              </span>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '3px 8px', cursor: page >= totalPages ? 'not-allowed' : 'pointer', color: page >= totalPages ? 'var(--text-muted)' : 'var(--text-secondary)', fontSize: 10, fontWeight: 700, opacity: page >= totalPages ? .4 : 1 }}>
                →
              </button>
            </div>
          </>
        )}
      </StatsRow>

      {/* ── Pending changes bar ── */}
      {pendingCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 24px', background: 'rgba(245,158,11,.08)', borderBottom: '2px solid #f59e0b' }}>
          <AlertCircle size={13} style={{ color: '#f59e0b' }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#f59e0b' }}>{pendingCount} modification{pendingCount > 1 ? 's' : ''} non sauvegardée{pendingCount > 1 ? 's' : ''}</span>
          <div style={{ flex: 1 }} />
          <SmallBtn onClick={discardPendingChanges} style={{ borderColor: '#ef4444', color: '#ef4444' }}>
            <Undo2 size={11} /> Annuler
          </SmallBtn>
          <SmallBtn $primary onClick={savePendingChanges} style={{ background: '#f59e0b', borderColor: '#f59e0b' }}>
            <Save size={11} /> Sauvegarder
          </SmallBtn>
        </div>
      )}

      {/* ── Active filters indicator ── */}
      {(filterStatuses.length > 0 || filterSources.length > 0 || filterSales || filterCategory) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 24px', background: 'rgba(165,145,239,.04)', borderBottom: '1px solid var(--border)' }}>
          <Filter size={11} style={{ color: 'var(--primary)' }} />
          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>Filtres actifs</span>
          <SmallBtn onClick={() => { setFilterStatuses([]); setFilterSources([]); setFilterSales(''); setFilterCategory(''); setPage(1); }}
            style={{ padding: '3px 8px', fontSize: 9 }}>
            <X size={9} /> Tout réinitialiser
          </SmallBtn>
        </div>
      )}

      {/* ── Mass action bar ── */}
      {selectedIds.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 24px', background: 'rgba(165,145,239,.08)', borderBottom: '2px solid var(--primary)', position: 'sticky', top: 0, zIndex: 20 }}>
          <CheckSquare size={14} style={{ color: 'var(--primary)' }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)' }}>{selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>
          <div style={{ flex: 1 }} />
          <SmallBtn onClick={massEnrich} disabled={massEnriching}
            style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', color: '#fff', borderColor: '#8b5cf6' }}>
            {massEnriching ? <Loader2 size={11} className="spin" /> : <Sparkles size={11} />} Enrichir ({selectedIds.size})
          </SmallBtn>
          {STATUSES.slice(0, 4).map(s => (
            <SmallBtn key={s} onClick={() => massStatusChange(s)} style={{ fontSize: 9, padding: '3px 8px' }}>
              {STATUS_LABELS[s]}
            </SmallBtn>
          ))}
          <SmallBtn onClick={massDelete} style={{ borderColor: '#ef4444', color: '#ef4444' }}>
            <Trash2 size={11} /> Supprimer
          </SmallBtn>
          <SmallBtn onClick={() => setSelectedIds(new Set())} style={{ padding: '3px 8px' }}>
            <X size={11} /> Désélectionner
          </SmallBtn>
        </div>
      )}

      {/* ── Content: Table (compact) or Cards (expanded) ── */}
      {viewMode === 'expanded' ? (
        /* ═══ EXPANDED CARD VIEW ═══ */
        <>
        {/* Filter bar for card view */}
        <div style={{ display: 'flex', gap: 6, padding: '8px 16px', flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
          <FilterInput style={{ maxWidth: 140 }} placeholder="@user..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <FilterInput style={{ maxWidth: 120 }} list="dl-cat-card" placeholder="Catégorie..." value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setPage(1); }} />
          <datalist id="dl-cat-card">{CATEGORIES.map(c => <option key={c} value={c} />)}</datalist>
          <FilterInput style={{ maxWidth: 100 }} list="dl-city-card" placeholder="Ville..." value={filterCity} onChange={e => { setFilterCity(e.target.value); setPage(1); }} />
          <datalist id="dl-city-card">{cities.map(c => <option key={c} value={c} />)}</datalist>
          <FilterInput style={{ maxWidth: 120 }} placeholder="Email..." value={filterEmail} onChange={e => { setFilterEmail(e.target.value); setPage(1); }} />
          <FilterInput style={{ maxWidth: 100 }} placeholder="Tél..." value={filterPhone} onChange={e => { setFilterPhone(e.target.value); setPage(1); }} />
          <FilterInput style={{ maxWidth: 120 }} placeholder="Adresse..." value={filterAddress} onChange={e => { setFilterAddress(e.target.value); setPage(1); }} />
          <FilterInput style={{ maxWidth: 120 }} list="dl-sales-card" placeholder="Commercial..." value={filterSales} onChange={e => { setFilterSales(e.target.value); setPage(1); }} />
          <datalist id="dl-sales-card">{salesAccounts.map(s => <option key={s} value={s} />)}</datalist>
        </div>
        <CardList>
          {prospects.length === 0 && !loading ? (
            <EmptyState>
              <Contact size={48} />
              <h3>Aucun prospect</h3>
              <p>Ajoutez des prospects manuellement ou importez-les depuis la recherche de profils</p>
            </EmptyState>
          ) : prospects.map(p => {
            const fmtNum = (n) => {
              if (n == null) return '—';
              if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
              if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
              return String(n);
            };
            const salesColor = getSalesColor(p.sales_account);
            return (
              <React.Fragment key={p.id}>
                <ProspectCard $bg={STATUS_ROW_BG[p.status]} $selected={selectedIds.has(p.id)}>
                  {/* Checkbox */}
                  <div style={{ position: 'absolute', top: 8, left: 8, cursor: 'pointer' }} onClick={() => toggleSelect(p.id)}>
                    {selectedIds.has(p.id) ? <CheckSquare size={14} style={{ color: 'var(--primary)' }} /> : <Square size={14} style={{ color: 'var(--text-muted)', opacity: 0.3 }} />}
                  </div>

                  {/* Avatar */}
                  <CardAvatar style={{ marginLeft: 16 }}>
                    {p.insta_username ? (
                      <img src={getLocalProfilePicUrl(p.insta_username)} alt=""
                        onError={e => { if (!e.target.dataset.fb) { e.target.dataset.fb='1'; e.target.src=`https://unavatar.io/instagram/${p.insta_username}`; } else { e.target.style.display='none'; if(e.target.nextSibling) e.target.nextSibling.style.display='flex'; } }} />
                    ) : null}
                    <CardAvatarFallback style={p.insta_username ? { display: 'none' } : {}}>
                      {(p.insta_username || p.company_name || '?').charAt(0).toUpperCase()}
                    </CardAvatarFallback>
                  </CardAvatar>

                  {/* Body — two-column layout */}
                  <CardBody>
                    {/* Row 1: Identity + status + actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      {p.insta_full_name && <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{p.insta_full_name}</span>}
                      {p.insta_username && (
                        <InstaLink href={`https://instagram.com/${p.insta_username}`}
                          onClick={e => { e.preventDefault(); openInExternalBrowser(`https://instagram.com/${p.insta_username}`); }}
                          style={{ fontSize: 12 }}>
                          @{p.insta_username}
                        </InstaLink>
                      )}
                      <InlineStatusSelect $s={p.status} value={p.status}
                        onChange={e => updateStatus(p.id, e.target.value)} style={{ marginLeft: 4 }}>
                        {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                      </InlineStatusSelect>
                      {p.category && (
                        <CategoryBadge $bg={CATEGORY_COLORS[p.category] ? `${CATEGORY_COLORS[p.category]}18` : undefined}
                          $color={CATEGORY_COLORS[p.category]}>{p.category}</CategoryBadge>
                      )}
                      {p.is_verified && <span style={{ fontSize: 10, color: '#3b82f6' }} title="Vérifié">✓</span>}
                      {p.is_business_account && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: 'rgba(59,130,246,.1)', color: '#3b82f6', fontWeight: 700 }}>PRO</span>}
                      <div style={{ flex: 1 }} />
                      <ActionBtns style={{ flexDirection: 'row' }}>
                        <button onClick={() => openPanel(p.id, 'gmail')} title="Gmail"
                          style={expandedPanel?.prospectId === p.id && expandedPanel?.tab === 'gmail' ? { color: 'var(--primary)', background: 'rgba(165,145,239,.15)' } : {}}>
                          <Mail size={13} />
                        </button>
                        <button onClick={() => openPanel(p.id, 'calendar')} title="Calendar"
                          style={expandedPanel?.prospectId === p.id && expandedPanel?.tab === 'calendar' ? { color: '#4285f4', background: 'rgba(66,133,244,.15)' } : {}}>
                          <Calendar size={13} />
                        </button>
                        {p.insta_username && (
                          <button onClick={() => openPanel(p.id, 'profil')} title="Voir profil"
                            style={expandedPanel?.prospectId === p.id && expandedPanel?.tab === 'profil' ? { color: '#e1306c', background: 'rgba(225,48,108,.15)' } : {}}>
                            <Eye size={13} />
                          </button>
                        )}
                        <button onClick={() => {
                          if (!p.insta_username) return;
                          axios.post(`/api/prospects/enrich-address/${p.id}`)
                            .then(r => { if (r.data.success) { alert(`Enrichi: ${r.data.data?.company_name || '?'} → ${r.data.data?.address || 'pas d\'adresse'}`); fetchProspects(); } else alert(r.data.error || 'Rien trouvé'); })
                            .catch(e => alert(e.message));
                        }} title="Enrichir avec Gemini"><Sparkles size={13} /></button>
                        <button onClick={() => openEdit(p)} title="Modifier"><Edit3 size={13} /></button>
                        <button onClick={() => deleteProspect(p.id)} title="Supprimer"><Trash2 size={13} /></button>
                      </ActionBtns>
                      {p.last_status_update && <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{new Date(p.last_status_update).toLocaleDateString('fr-FR')}</span>}
                    </div>

                    {/* Row 2: Two-column info grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginTop: 4 }}>
                      {/* Left column: Bio + Stats */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {p.insta_bio && <CardBio title={p.insta_bio}>{p.insta_bio}</CardBio>}
                        <CardStatsRow>
                          {p.followers != null && <CardStat><Users size={10} /> <span>{fmtNum(p.followers)}</span></CardStat>}
                          {p.following != null && <CardStat><UserCircle size={10} /> <span>{fmtNum(p.following)}</span></CardStat>}
                          {p.posts != null && <CardStat><Hash size={10} /> <span>{fmtNum(p.posts)}</span></CardStat>}
                          {p.sales_account && <CardStat><span style={{ width: 7, height: 7, borderRadius: '50%', background: salesColor, display: 'inline-block' }} /> {p.sales_account}</CardStat>}
                        </CardStatsRow>
                        {p.comment && (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', padding: '4px 8px', borderRadius: 6, background: 'var(--surface)', border: '1px solid var(--border)', lineHeight: 1.4, maxHeight: 40, overflow: 'hidden' }} title={p.comment}>
                            <span style={{ fontWeight: 700, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.3px' }}>Note:</span> {p.comment}
                          </div>
                        )}
                      </div>

                      {/* Right column: Contact + Address */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {p.email && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--text-secondary)', padding: '3px 8px', borderRadius: 4, background: 'rgba(34,197,94,.05)', border: '1px solid rgba(34,197,94,.15)' }}>
                            <Mail size={10} style={{ color: '#22c55e', flexShrink: 0 }} /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.email}</span>
                          </div>
                        )}
                        {p.phone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--text-secondary)', padding: '3px 8px', borderRadius: 4, background: 'rgba(59,130,246,.05)', border: '1px solid rgba(59,130,246,.15)' }}>
                            <Phone size={10} style={{ color: '#3b82f6', flexShrink: 0 }} /> {p.phone}
                          </div>
                        )}
                        {(p.address || p.city) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--text-secondary)', padding: '3px 8px', borderRadius: 4, background: 'rgba(245,158,11,.05)', border: '1px solid rgba(245,158,11,.15)' }}>
                            <MapPin size={10} style={{ color: '#f59e0b', flexShrink: 0 }} /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.address || p.city}{p.address && p.city ? '' : ''}</span>
                          </div>
                        )}
                        {p.website && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: 'var(--text-secondary)', padding: '3px 8px', borderRadius: 4, background: 'rgba(165,145,239,.05)', border: '1px solid rgba(165,145,239,.15)' }}>
                            <Globe size={10} style={{ color: 'var(--primary)', flexShrink: 0 }} /> <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.website}</span>
                          </div>
                        )}
                        {!p.email && !p.phone && !p.address && !p.city && !p.website && (
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', fontStyle: 'italic', padding: '3px 8px' }}>Pas de coordonnées</div>
                        )}
                      </div>
                    </div>

                    {/* Message area */}
                    {p.first_message_sent && (
                      <CardMsgArea>{p.first_message_sent}</CardMsgArea>
                    )}
                    {!p.first_message_sent && (
                      <CardMsgArea />
                    )}
                  </CardBody>
                </ProspectCard>
                {/* Comm panel below card if open */}
                {expandedPanel?.prospectId === p.id && (
                  <div style={{ margin: '-4px 0 4px', borderRadius: '0 0 12px 12px', overflow: 'hidden', border: '1px solid var(--primary)', borderTop: 'none' }}>
                    <table style={{ width: '100%' }}><tbody>{renderCommPanel(p)}</tbody></table>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </CardList>
        </>
      ) : (
      /* ═══ COMPACT TABLE VIEW ═══ */
      <TableWrap>
        {prospects.length === 0 && !loading ? (
          <EmptyState>
            <Contact size={48} />
            <h3>Aucun prospect</h3>
            <p>Ajoutez des prospects manuellement ou importez-les depuis la recherche de profils</p>
          </EmptyState>
        ) : (
          <Table style={{ '--pin-offset': `${totalPinnedHeight}px` }}>
            <thead>
              <tr>
                <th style={{ width: 28, cursor: 'pointer', textAlign: 'center' }} onClick={toggleSelectAll}>
                  {allSelected ? <CheckSquare size={13} style={{ color: 'var(--primary)' }} /> : <Square size={13} />}
                </th>
                <th onClick={() => handleSort('status')} style={{ width: 72 }}>Statut<SortIcon col="status" /></th>
                <th onClick={() => handleSort('insta_username')} style={{ width: 120 }}>Instagram<SortIcon col="insta_username" /></th>
                <th onClick={() => handleSort('category')} style={{ width: 88 }}>Catégorie<SortIcon col="category" /></th>
                <th onClick={() => handleSort('categories')} className="col-hide-1" style={{ width: 68 }}>Sous-cat.<SortIcon col="categories" /></th>
                <th onClick={() => handleSort('city')} style={{ width: 72 }}>Ville<SortIcon col="city" /></th>
                <th onClick={() => handleSort('address')} className="col-hide-1" style={{ width: 90 }}>Adresse<SortIcon col="address" /></th>
                <th onClick={() => handleSort('email')} style={{ width: 110 }}>Email<SortIcon col="email" /></th>
                <th onClick={() => handleSort('phone')} className="col-hide-2" style={{ width: 72 }}>Tél<SortIcon col="phone" /></th>
                <th onClick={() => handleSort('sales_account')} className="col-hide-2" style={{ width: 78 }}>Comm.<SortIcon col="sales_account" /></th>
                <th onClick={() => handleSort('source')} className="col-hide-1" style={{ width: 58 }}>Source<SortIcon col="source" /></th>
                <th onClick={() => handleSort('comment')} className="col-hide-1" style={{ width: 80 }}>Note<SortIcon col="comment" /></th>
                <th onClick={() => handleSort('last_status_update')} className="col-hide-2" style={{ width: 56 }}>MAJ<SortIcon col="last_status_update" /></th>
                <th style={{ width: 95 }}>Act.</th>
              </tr>
              {/* ── Column filter row ── */}
              <tr>
                <FilterTh />
                <FilterTh>
                  <CheckboxDropdown options={STATUSES} selected={filterStatuses}
                    onChange={v => { setFilterStatuses(v); setPage(1); }}
                    labels={STATUS_LABELS} placeholder="Tous" />
                </FilterTh>
                <FilterTh>
                  <FilterInput placeholder="@user..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
                </FilterTh>
                <FilterTh>
                  <FilterInput list="dl-cat" placeholder="Catégorie..." value={filterCategory}
                    onChange={e => { setFilterCategory(e.target.value); setPage(1); }} />
                  <datalist id="dl-cat">
                    {CATEGORIES.map(c => <option key={c} value={c} />)}
                  </datalist>
                </FilterTh>
                <FilterTh className="col-hide-1">
                  <FilterInput placeholder="Sous-cat..." value={filterSubCategory}
                    onChange={e => { setFilterSubCategory(e.target.value); setPage(1); }} />
                </FilterTh>
                <FilterTh>
                  <FilterInput list="dl-city" placeholder="Ville..." value={filterCity}
                    onChange={e => { setFilterCity(e.target.value); setPage(1); }} />
                  <datalist id="dl-city">
                    {cities.map(c => <option key={c} value={c} />)}
                  </datalist>
                </FilterTh>
                <FilterTh className="col-hide-1"><FilterInput placeholder="Adresse..." value={filterAddress}
                  onChange={e => { setFilterAddress(e.target.value); setPage(1); }} /></FilterTh>
                <FilterTh><FilterInput placeholder="Email..." value={filterEmail}
                  onChange={e => { setFilterEmail(e.target.value); setPage(1); }} /></FilterTh>
                <FilterTh className="col-hide-2"><FilterInput placeholder="Tél..." value={filterPhone}
                  onChange={e => { setFilterPhone(e.target.value); setPage(1); }} /></FilterTh>
                <FilterTh className="col-hide-2">
                  <FilterInput list="dl-sales" placeholder="Commercial..." value={filterSales}
                    onChange={e => { setFilterSales(e.target.value); setPage(1); }} />
                  <datalist id="dl-sales">
                    {salesAccounts.map(s => <option key={s} value={s} />)}
                  </datalist>
                </FilterTh>
                <FilterTh className="col-hide-1">
                  <CheckboxDropdown options={SOURCES} selected={filterSources}
                    onChange={v => { setFilterSources(v); setPage(1); }}
                    placeholder="Tous" />
                </FilterTh>
                <FilterTh className="col-hide-1" />
                <FilterTh className="col-hide-2" />
                <FilterTh />
              </tr>
            </thead>
            <tbody>
              {prospects.map(p => (
                <React.Fragment key={p.id}>
                <tr style={{ background: expandedPanel?.prospectId === p.id ? 'rgba(165,145,239,.08)' : selectedIds.has(p.id) ? 'rgba(165,145,239,.06)' : (STATUS_ROW_BG[p.status] || 'transparent') }}>
                  {/* Checkbox */}
                  <td style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSelect(p.id)}>
                    {selectedIds.has(p.id) ? <CheckSquare size={13} style={{ color: 'var(--primary)' }} /> : <Square size={13} style={{ color: 'var(--text-muted)', opacity: 0.4 }} />}
                  </td>
                  {/* Status — inline select */}
                  <td>
                    <InlineStatusSelect $s={p.status} value={p.status}
                      onChange={e => updateStatus(p.id, e.target.value)}>
                      {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </InlineStatusSelect>
                  </td>
                  {/* Instagram — profile pic + editable username */}
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {p.insta_username && (
                        <img src={getLocalProfilePicUrl(p.insta_username)} alt=""
                          onError={e => { if (!e.target.dataset.fb) { e.target.dataset.fb='1'; e.target.src=`https://unavatar.io/instagram/${p.insta_username}`; } else { e.target.style.display='none'; if(e.target.nextSibling) e.target.nextSibling.style.display='flex'; } }}
                          style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--primary)', flexShrink: 0 }} />
                      )}
                      {p.insta_username && (
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--primary)', display: 'none', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                          {p.insta_username.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <EditableCell value={p.insta_username} field="insta_username" rowId={p.id} onSave={updateField}
                        render={v => v ? <InstaLink href={`https://instagram.com/${v}`} onClick={e => { e.preventDefault(); e.stopPropagation(); openInExternalBrowser(`https://instagram.com/${v}`); }}>@{v}</InstaLink> : '—'} />
                    </div>
                  </td>
                  {/* Category — editable select */}
                  <td>
                    <EditableCell value={p.category} field="category" rowId={p.id} onSave={updateField}
                      options={CATEGORIES}
                      render={v => v ? (
                        <CategoryBadge $bg={CATEGORY_COLORS[v] ? `${CATEGORY_COLORS[v]}18` : undefined}
                          $color={CATEGORY_COLORS[v] || undefined}>{v}</CategoryBadge>
                      ) : '—'} />
                  </td>
                  {/* Sub-categories — editable tags */}
                  <td className="col-hide-1">
                    <EditableCell value={Array.isArray(p.sub_categories) ? p.sub_categories.join(', ') : (p.sub_categories || '')} field="sub_categories" rowId={p.id} onSave={(id, field, val) => {
                      const arr = val ? val.split(',').map(s => s.trim()).filter(Boolean) : null;
                      updateField(id, field, arr);
                    }}
                      render={v => {
                        const tags = v ? v.split(',').map(s => s.trim()).filter(Boolean) : [];
                        return tags.length > 0 ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            {tags.slice(0, 3).map(t => <span key={t} style={{ fontSize: 8, padding: '1px 4px', borderRadius: 3, background: 'rgba(165,145,239,.1)', color: 'var(--primary)', border: '1px solid rgba(165,145,239,.2)' }}>{t}</span>)}
                            {tags.length > 3 && <span style={{ fontSize: 8, color: 'var(--text-muted)' }}>+{tags.length - 3}</span>}
                          </div>
                        ) : '—';
                      }} />
                  </td>
                  {/* City — editable text */}
                  <td><EditableCell value={p.city} field="city" rowId={p.id} onSave={updateField} /></td>
                  {/* Address — editable text */}
                  <td className="col-hide-1"><EditableCell value={p.address} field="address" rowId={p.id} onSave={updateField}
                    render={v => <span style={{ fontSize: 10, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }} title={v || ''}>{v || '—'}</span>} /></td>
                  {/* Email — editable text */}
                  <td>
                    <EditableCell value={p.email} field="email" rowId={p.id} onSave={updateField}
                      render={v => v ? <a href={`mailto:${v}`} style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 10 }} onClick={e => e.stopPropagation()}><Mail size={9} style={{ marginRight: 3 }} />{v}</a> : '—'} />
                  </td>
                  {/* Phone — editable text */}
                  <td className="col-hide-2">
                    <EditableCell value={p.phone} field="phone" rowId={p.id} onSave={updateField}
                      render={v => v ? <a href={`tel:${v}`} style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 10 }} onClick={e => e.stopPropagation()}><Phone size={9} style={{ marginRight: 3 }} />{v}</a> : '—'} />
                  </td>
                  {/* Commercial — editable text with colored badge */}
                  <td className="col-hide-2"><EditableCell value={p.sales_account} field="sales_account" rowId={p.id} onSave={updateField}
                    render={v => {
                      if (!v) return '—';
                      const c = getSalesColor(v);
                      return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: `${c}18`, color: c, whiteSpace: 'nowrap' }}>{v}</span>;
                    }} /></td>
                  {/* Source — editable select */}
                  <td className="col-hide-1">
                    <EditableCell value={p.source} field="source" rowId={p.id} onSave={updateField}
                      options={SOURCES} render={v => <span style={{ fontSize: 10 }}>{v || '—'}</span>} />
                  </td>
                  {/* Comment — editable text */}
                  <td className="col-hide-1">
                    <EditableCell value={p.comment} field="comment" rowId={p.id} onSave={updateField}
                      render={v => <span style={{ fontSize: 10, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}
                        title={v || ''}>{v || '—'}</span>} />
                  </td>
                  {/* Last update — display only */}
                  <td className="col-hide-2" style={{ fontSize: 10 }}>
                    {p.last_status_update ? new Date(p.last_status_update).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  {/* Actions */}
                  <td>
                    <ActionBtns>
                      <button onClick={() => openPanel(p.id, 'gmail')} title="Gmail" style={expandedPanel?.prospectId === p.id && expandedPanel?.tab === 'gmail' ? { color: 'var(--primary)', background: 'rgba(165,145,239,.15)' } : {}}>
                        <Mail size={13} />
                      </button>
                      <button onClick={() => openPanel(p.id, 'calendar')} title="Calendar" style={expandedPanel?.prospectId === p.id && expandedPanel?.tab === 'calendar' ? { color: '#4285f4', background: 'rgba(66,133,244,.15)' } : {}}>
                        <Calendar size={13} />
                      </button>
                      {p.insta_username && (
                        <button onClick={() => openPanel(p.id, 'profil')} title="Voir profil" style={expandedPanel?.prospectId === p.id && expandedPanel?.tab === 'profil' ? { color: '#e1306c', background: 'rgba(225,48,108,.15)' } : {}}>
                          <Eye size={13} />
                        </button>
                      )}
                      <button onClick={() => {
                        if (!p.insta_username) return;
                        axios.post(`/api/prospects/enrich-address/${p.id}`)
                          .then(r => { if (r.data.success) { alert(`Enrichi: ${r.data.data?.company_name || '?'} → ${r.data.data?.address || 'pas d\'adresse'}`); fetchProspects(); } else alert(r.data.error || 'Rien trouvé'); })
                          .catch(e => alert(e.message));
                      }} title="Enrichir avec Gemini"><Sparkles size={13} /></button>
                      <button onClick={() => openEdit(p)} title="Modifier"><Edit3 size={13} /></button>
                      <button onClick={() => deleteProspect(p.id)} title="Supprimer"><Trash2 size={13} /></button>
                    </ActionBtns>
                  </td>
                </tr>
                {renderCommPanel(p)}
                </React.Fragment>
              ))}
            </tbody>
          </Table>
        )}
      </TableWrap>
      )}

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <Pagination>
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Précédent</button>
          <span>Page {page} / {totalPages}</span>
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant →</button>
        </Pagination>
      )}

      {/* ── Add/Edit Modal ── */}
      {modalOpen && (
        <ModalOverlay onClick={() => setModalOpen(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <h2>{editingId ? 'Modifier le prospect' : 'Nouveau prospect'}</h2>
              <button onClick={() => setModalOpen(false)}><X size={18} /></button>
            </ModalHeader>
            <ModalBody>
              <FormField>
                <label><Instagram size={10} /> Instagram</label>
                <input placeholder="@username" value={form.insta_username}
                  onChange={e => up('insta_username', e.target.value)} />
              </FormField>
              <FormField>
                <label><Building2 size={10} /> Entreprise</label>
                <input placeholder="Nom de l'entreprise" value={form.company_name}
                  onChange={e => up('company_name', e.target.value)} />
              </FormField>
              <FormField>
                <label><Linkedin size={10} /> LinkedIn</label>
                <input placeholder="Nom LinkedIn" value={form.linkedin_name}
                  onChange={e => up('linkedin_name', e.target.value)} />
              </FormField>
              <FormField>
                <label>Commercial</label>
                <input placeholder="sales_account" value={form.sales_account}
                  onChange={e => up('sales_account', e.target.value)} />
              </FormField>
              <FormField>
                <label>Statut</label>
                <select value={form.status} onChange={e => up('status', e.target.value)}>
                  {STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </FormField>
              <FormField>
                <label>Priorité</label>
                <select value={form.priority} onChange={e => up('priority', e.target.value)}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </FormField>
              <FormField>
                <label>Source</label>
                <select value={form.source} onChange={e => up('source', e.target.value)}>
                  {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </FormField>
              <FormField>
                <label>Détail source</label>
                <input placeholder="Ex: Salon Paris 2026" value={form.source_detail}
                  onChange={e => up('source_detail', e.target.value)} />
              </FormField>
              <FormField>
                <label><Tag size={10} /> Catégorie</label>
                <select value={form.category} onChange={e => up('category', e.target.value)}>
                  <option value="">— Sélectionner —</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </FormField>
              <FormField>
                <label>Sous-catégories</label>
                <input placeholder="cat1, cat2, cat3..." value={form.categories}
                  onChange={e => up('categories', e.target.value)} />
              </FormField>
              <FormField>
                <label><MapPin size={10} /> Ville</label>
                <input value={form.city} onChange={e => up('city', e.target.value)} />
              </FormField>
              <FormField>
                <label>Adresse</label>
                <input value={form.address} onChange={e => up('address', e.target.value)} />
              </FormField>
              <FormField>
                <label><Phone size={10} /> Téléphone</label>
                <input value={form.phone} onChange={e => up('phone', e.target.value)} />
              </FormField>
              <FormField>
                <label><Mail size={10} /> Email</label>
                <input type="email" value={form.email} onChange={e => up('email', e.target.value)} />
              </FormField>
              <FormField>
                <label><ExternalLink size={10} /> Site web</label>
                <input value={form.website} onChange={e => up('website', e.target.value)} />
              </FormField>
              <FormField>
                <label><DollarSign size={10} /> Budget</label>
                <input placeholder="500-2000€" value={form.budget_range}
                  onChange={e => up('budget_range', e.target.value)} />
              </FormField>
              <FormField>
                <label>Service intéressé</label>
                <input value={form.service_interest} onChange={e => up('service_interest', e.target.value)} />
              </FormField>
              <FormField>
                <label>Valeur deal (€)</label>
                <input type="number" value={form.deal_value} onChange={e => up('deal_value', e.target.value)} />
              </FormField>
              <FormField>
                <label>Posts</label>
                <input type="number" value={form.posts} onChange={e => up('posts', e.target.value)} />
              </FormField>
              <FormField>
                <label>Followers</label>
                <input type="number" value={form.followers} onChange={e => up('followers', e.target.value)} />
              </FormField>
              <FormField>
                <label>Following</label>
                <input type="number" value={form.following} onChange={e => up('following', e.target.value)} />
              </FormField>
              <FormField>
                <label>Langue</label>
                <input placeholder="fr, en..." value={form.language} onChange={e => up('language', e.target.value)} />
              </FormField>
              <FormField $full>
                <label><MessageCircle size={10} /> 1er message envoyé</label>
                <textarea value={form.first_message_sent}
                  onChange={e => up('first_message_sent', e.target.value)} />
              </FormField>
              <FormField $full>
                <label>Commentaire</label>
                <textarea value={form.comment} onChange={e => up('comment', e.target.value)} />
              </FormField>
            </ModalBody>
            <ModalFooter>
              <SmallBtn onClick={() => setModalOpen(false)}>Annuler</SmallBtn>
              <SmallBtn $primary onClick={saveProspect}>
                <Check size={12} /> {editingId ? 'Enregistrer' : 'Créer'}
              </SmallBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── CSV Import Modal ── */}
      {importModalOpen && (
        <ModalOverlay onClick={() => setImportModalOpen(false)}>
          <ModalBox onClick={e => e.stopPropagation()} style={{ width: '700px', maxHeight: '85vh' }}>
            <ModalHeader>
              <h2><FileSpreadsheet size={16} style={{ marginRight: 8 }} /> Importer depuis CSV / Google Sheets</h2>
              <button onClick={() => setImportModalOpen(false)}><X size={18} /></button>
            </ModalHeader>
            <div style={{ padding: '20px 24px' }}>
              {/* Step 1: File upload */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.tsv,.txt"
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />
              <ImportDropZone
                $active={csvRows.length > 0}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  const file = e.dataTransfer.files?.[0];
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const { headers, rows } = parseCSV(ev.target.result);
                      setCsvHeaders(headers);
                      setCsvRows(rows);
                      setCsvMapping(buildMapping(headers));
                      setImportResult(null);
                    };
                    reader.readAsText(file, 'UTF-8');
                  }
                }}
              >
                <Upload size={32} />
                {csvRows.length > 0 ? (
                  <>
                    <strong>{csvRows.length} ligne(s) chargée(s)</strong>
                    <p>Cliquer pour changer de fichier</p>
                  </>
                ) : (
                  <>
                    <strong>Glisser-déposer ou cliquer pour sélectionner</strong>
                    <p>Formats acceptés : CSV, TSV (export Google Sheets)</p>
                    <p style={{ fontSize: 11 }}>Google Sheets → Fichier → Télécharger → CSV (.csv)</p>
                  </>
                )}
              </ImportDropZone>

              {/* Step 2: Column mapping */}
              {csvMapping.length > 0 && (
                <>
                  <ImportStats>
                    <div>Colonnes détectées : <span>{csvHeaders.length}</span></div>
                    <div>Colonnes mappées : <span>{csvMapping.filter(m => m.prospectField).length}</span></div>
                    <div>Lignes à importer : <span>{csvRows.length}</span></div>
                  </ImportStats>

                  <MappingTable>
                    <thead>
                      <tr>
                        <th>Colonne CSV</th>
                        <th>Champ CRM</th>
                        <th>Aperçu (1ère ligne)</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvMapping.map((m, i) => (
                        <tr key={i}>
                          <td style={{ fontWeight: 600 }}>{m.csvHeader}</td>
                          <td>{m.prospectField || '—'}</td>
                          <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {csvRows[0]?.[i] || '—'}
                          </td>
                          <td>
                            <MappedBadge $mapped={!!m.prospectField}>
                              {m.prospectField ? 'Mappé' : 'Ignoré'}
                            </MappedBadge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </MappingTable>

                  {/* Preview */}
                  {csvRows.length > 0 && (
                    <PreviewScroll>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                        <thead>
                          <tr>
                            {csvHeaders.map((h, i) => (
                              <th key={i} style={{ padding: '4px 6px', background: 'var(--surface)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', fontSize: 10, fontWeight: 700, color: csvMapping[i]?.prospectField ? 'var(--primary)' : 'var(--text-muted)' }}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {csvRows.slice(0, 5).map((row, ri) => (
                            <tr key={ri}>
                              {row.map((cell, ci) => (
                                <td key={ci} style={{ padding: '3px 6px', borderBottom: '1px solid var(--border)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>
                                  {cell || '—'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </PreviewScroll>
                  )}
                </>
              )}

              {/* Import result */}
              {importResult && (
                <div style={{ marginTop: 16, padding: 12, borderRadius: 8, background: importResult.error ? 'rgba(239,68,68,.1)' : 'rgba(34,197,94,.1)', border: `1px solid ${importResult.error ? 'rgba(239,68,68,.3)' : 'rgba(34,197,94,.3)'}` }}>
                  {importResult.error ? (
                    <div style={{ color: '#ef4444', fontWeight: 600, fontSize: 13 }}>
                      <AlertCircle size={14} style={{ marginRight: 6 }} /> Erreur : {importResult.error}
                    </div>
                  ) : (
                    <div style={{ color: '#22c55e', fontWeight: 600, fontSize: 13 }}>
                      <Check size={14} style={{ marginRight: 6 }} />
                      {importResult.imported} prospect(s) importé(s)
                      {importResult.skipped > 0 && ` — ${importResult.skipped} déjà existant(s)`}
                    </div>
                  )}
                </div>
              )}
            </div>
            <ModalFooter>
              <SmallBtn onClick={() => setImportModalOpen(false)}>Fermer</SmallBtn>
              <SmallBtn
                $primary
                onClick={handleCsvImport}
                disabled={csvRows.length === 0 || importing || (importResult && !importResult.error)}
              >
                {importing ? (
                  <><RefreshCw size={12} className="spin" /> Import en cours...</>
                ) : (
                  <><Upload size={12} /> Importer {csvRows.length} ligne(s)</>
                )}
              </SmallBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Action Modal (Email / Calendar) — replaces window.prompt for Electron ── */}
      {actionModal && (
        <ModalOverlay onClick={() => setActionModal(null)}>
          <ModalBox onClick={e => e.stopPropagation()} style={{ width: 440 }}>
            <ModalHeader>
              <h2>{actionModal.type === 'email' ? '📧 Envoyer un email' : '📅 Planifier un RDV'}</h2>
              <button onClick={() => setActionModal(null)}><X size={18} /></button>
            </ModalHeader>
            <ModalBody style={{ gridTemplateColumns: '1fr' }}>
              {actionModal.type === 'email' ? (
                <>
                  <FormField>
                    <label>Destinataire</label>
                    <input value={actionModal.prospect?.email || ''} disabled style={{ opacity: .6 }} />
                  </FormField>
                  <FormField>
                    <label>Objet</label>
                    <input value={actionForm.subject || ''} onChange={e => setActionForm(f => ({ ...f, subject: e.target.value }))} />
                  </FormField>
                  <FormField>
                    <label>Corps (HTML)</label>
                    <textarea rows={4} value={actionForm.body || ''} onChange={e => setActionForm(f => ({ ...f, body: e.target.value }))} />
                  </FormField>
                </>
              ) : (
                <>
                  <FormField>
                    <label>Titre du RDV</label>
                    <input value={actionForm.summary || ''} onChange={e => setActionForm(f => ({ ...f, summary: e.target.value }))} />
                  </FormField>
                  <FormField>
                    <label>Date</label>
                    <input type="date" value={actionForm.date || ''} onChange={e => setActionForm(f => ({ ...f, date: e.target.value }))} />
                  </FormField>
                  <FormField>
                    <label>Heure</label>
                    <input type="time" value={actionForm.time || ''} onChange={e => setActionForm(f => ({ ...f, time: e.target.value }))} />
                  </FormField>
                </>
              )}
            </ModalBody>
            <ModalFooter>
              <SmallBtn onClick={() => setActionModal(null)}>Annuler</SmallBtn>
              <SmallBtn $primary onClick={() => {
                const p = actionModal.prospect;
                if (actionModal.type === 'email') {
                  axios.post('/api/google/gmail/send', { to: p.email, subject: actionForm.subject, body: actionForm.body, prospect_id: p.id })
                    .then(r => { if (r.data.success) { setActionModal(null); alert('✅ Email envoyé !'); } else alert('❌ ' + (r.data.error || 'Erreur')); })
                    .catch(e => alert('❌ ' + (e.response?.data?.error || e.message)));
                } else {
                  const start = `${actionForm.date}T${actionForm.time}:00`;
                  const h = parseInt(actionForm.time.split(':')[0]) + 1;
                  const end = `${actionForm.date}T${String(h).padStart(2,'0')}:${actionForm.time.split(':')[1]}:00`;
                  axios.post('/api/google/calendar/event', {
                    summary: actionForm.summary, start, end, prospect_id: p.id,
                    location: p.address || p.city || '',
                    attendees: p.email ? [p.email] : [],
                    description: `Prospect: ${p.insta_username || ''}\n${p.company_name || ''}\n${p.phone || ''}`
                  })
                    .then(r => { if (r.data.success) { setActionModal(null); alert('✅ RDV créé !'); } else alert('❌ ' + (r.data.error || 'Erreur')); })
                    .catch(e => alert('❌ ' + (e.response?.data?.error || e.message)));
                }
              }}>
                <Check size={12} /> {actionModal.type === 'email' ? 'Envoyer' : 'Créer le RDV'}
              </SmallBtn>
            </ModalFooter>
          </ModalBox>
        </ModalOverlay>
      )}
    </Page>
  );
};

export default CrmProspects;
